import React, { useMemo } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { DiscoverCircleIcon, Location01Icon, Setting07Icon } from "@hugeicons/core-free-icons";
import { useLanguage } from "../../../contexts/LanguageContext";
import { getServicesData } from "./servicesData";

const ServicesLanding: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const [searchParams] = useSearchParams();
  const queryRedirect = (searchParams.get("q") || "").trim();

  const servicesData = useMemo(() => getServicesData(t), [t]);
  const visibleServices = servicesData;
  const servicesGridClass = useMemo(() => {
    const count = visibleServices.length;

    if (count <= 1) return "grid-cols-1 max-w-sm";
    if (count === 2) return "grid-cols-1 md:grid-cols-2 max-w-4xl";
    if (count === 3) return "grid-cols-1 md:grid-cols-3 max-w-7xl";
    if (count === 4) return "grid-cols-1 md:grid-cols-2 max-w-5xl";

    return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 max-w-7xl";
  }, [visibleServices.length]);

  if (queryRedirect) {
    return <Navigate to={`/search?q=${encodeURIComponent(queryRedirect)}`} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <header className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('servicesPageTitle')}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('servicesPageDesc')}
            </p>
          </header>

          <div className="flex justify-center">
            <div className={`grid auto-rows-fr gap-8 w-full ${servicesGridClass}`}>
              {visibleServices.map((service) => (
              <Link
                key={service.id}
                to={service.link}
                className="group block h-full focus:outline-none focus-visible:ring-4 focus-visible:ring-sa-200 rounded-2xl"
              >
                <article className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 md:p-8 hover:shadow-xl transition-transform duration-300 group-hover:-translate-y-1 flex flex-col h-full min-h-[360px]">
                <div className={`w-12 h-12 flex items-center justify-center mb-4 ${isRTL ? "self-end" : "self-start"}`}>
                  {service.icon === "land" ? (
                    <span className="text-sa-600" aria-hidden="true">
                      <HugeiconsIcon icon={DiscoverCircleIcon} size={28} color="currentColor" strokeWidth={1.8} />
                    </span>
                  ) : service.icon === "search" ? (
                    <span className="text-sa-600" aria-hidden="true">
                      <HugeiconsIcon icon={Location01Icon} size={28} color="currentColor" strokeWidth={1.8} />
                    </span>
                  ) : service.icon === "maintenance" ? (
                    <span className="text-sa-600" aria-hidden="true">
                      <HugeiconsIcon icon={Setting07Icon} size={28} color="currentColor" strokeWidth={1.8} />
                    </span>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      className="text-sa-600"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                  <h2 className={`text-xl font-bold text-gray-900 mb-3 group-hover:text-sa-700 transition-colors duration-300 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {service.title}
                  </h2>
                  <p className={`text-sm text-gray-600 leading-relaxed mb-4 flex-grow ${isRTL ? 'text-right' : 'text-left'}`}>
                    {service.description}
                  </p>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                    <span className="badge bg-success bg-opacity-10 text-success px-3 py-2">
                      {service.category}
                    </span>
                    <svg
                      className="w-5 h-5 text-sa-400 group-hover:text-sa-600 transition-colors duration-300"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 18l-6-6 6-6" />
                      <path d="M3 12h18" />
                    </svg>
                </div>
                </article>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesLanding;
